import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface DiscordLinkBannerProps {
  onNavigate?: () => void;
}

export const DiscordLinkBanner: React.FC<DiscordLinkBannerProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [hasDiscord, setHasDiscord] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if banner was dismissed
    const isDismissed = localStorage.getItem('discord-banner-dismissed') === 'true';
    setDismissed(isDismissed);

    // Check if user has Discord linked
    const checkDiscordLink = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('discord_id')
        .eq('id', user.id)
        .single();

      if (!error && data) {
        setHasDiscord(!!data.discord_id);
      }
      setLoading(false);
    };

    checkDiscordLink();
  }, [user]);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('discord-banner-dismissed', 'true');
  };

  // Don't show if dismissed, loading, or user already has Discord linked
  if (dismissed || loading || hasDiscord) {
    return null;
  }

  return (
    <div className="relative bg-gradient-to-r from-[#5865F2]/30 to-[#7289DA]/30 border-b border-[#5865F2]/40 overflow-hidden">
      {/* Mirrorman Mascot - Behind content */}
      <div className="absolute -bottom-2 right-0 w-28 h-28 pointer-events-none z-10">
        <img 
          src="https://guwcqpytunuquoirejsc.supabase.co/storage/v1/object/public/fomo-assets/mirrorman_intro.png" 
          alt="Mirrorman" 
          className="w-full h-full object-contain opacity-80"
        />
      </div>
      
      <div className="p-4 pr-10 relative z-0">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 text-2xl">
            💬
          </div>
          <div className="flex-1 space-y-2">
            <h4 className="text-sm font-bold text-white">
              Get Instant Notifications on Discord!
            </h4>
            <p className="text-xs text-gray-100 leading-relaxed">
              Link your Discord account to receive DMs for likes, comments, follows, and more. No mobile app needed!
            </p>
            <Button
              size="sm"
              variant="default"
              className="bg-[#5865F2] hover:bg-[#4752C4] text-white text-xs h-8 font-semibold shadow-sm"
              onClick={() => {
                onNavigate?.();
                navigate('/link-discord');
              }}
            >
              Link Discord
            </Button>
          </div>
        </div>
      </div>
      
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 h-6 w-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors z-20"
        aria-label="Dismiss"
      >
        <X className="h-3 w-3 text-white" />
      </button>
    </div>
  );
};
